import { useEffect } from "react";
import { safeLocalStorage } from "@/lib/storage";
import { useIsHydrated } from "./useIsHydrated";

// Early hydration renders (and the session skeletons) run before the dynamic
// route's query is parsed, so the org is briefly unknown even when the URL
// names one. Each resolved flag is therefore also cached under an org-less
// key that those renders fall back to.
const LAST_RESOLVED_ORG = "__last__";

export function readCachedOrgFlag(
  storageKeyPrefix: string,
  orgName: string | null,
): boolean {
  return (
    safeLocalStorage.get(storageKeyPrefix + (orgName ?? LAST_RESOLVED_ORG)) ===
    "true"
  );
}

/**
 * Cached view of a per-installation flag that rides on the installations
 * query. On every page load there is a window where the flag reads `false`
 * and gated UI would pop in late; until the flag is known this falls back to
 * the value cached from the last visit (orgs never seen before assume
 * disabled), and caches the resolved value once the backend answers.
 */
export function useCachedOrgFlag(
  storageKeyPrefix: string,
  {
    orgName,
    enabled,
    enabledKnown,
  }: {
    orgName: string | null;
    enabled: boolean;
    enabledKnown: boolean;
  },
): boolean {
  const isHydrated = useIsHydrated();

  useEffect(() => {
    if (!enabledKnown || !orgName) return;
    safeLocalStorage.set(storageKeyPrefix + orgName, String(enabled));
    safeLocalStorage.set(storageKeyPrefix + LAST_RESOLVED_ORG, String(enabled));
  }, [storageKeyPrefix, enabledKnown, orgName, enabled]);

  if (enabledKnown) return enabled;
  // The hydration render can't read storage without mismatching server HTML.
  if (!isHydrated) return false;
  return readCachedOrgFlag(storageKeyPrefix, orgName);
}
